import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDataManagement } from "../ferramentas/RequisitarAPI.ts";
import style from "./ModalStyles.module.css";
import ToastMessage from "../ferramentas/toastMessage/ToastMessage";
import { AgendamentoModal } from "./AgendamentoModal";
import ModalConfirmaExclusao from "../ferramentas/modais/modalConfirmaExclusao/modalConfirmaExclusao";
import { ReactComponent as CircuitLoader } from '../../img/loaders/circuit-board.svg';

const BulkAppointmentsModal = ({ isOpen, onClose, groupId }) => {
  const { appointments, load } = useDataManagement();
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [isBulkEditingMode, setIsBulkEditingMode] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (isOpen && groupId) {
        setAppointmentsList([]);
        setError(null);
        setHasChanged(false);
        try {
          const result = await appointments.getBulk(groupId);
          if (result.ok) {
            const appointmentsArray = Object.values(result.data);
            setAppointmentsList(appointmentsArray);
          } else {
            setError("Falha ao buscar agendamentos em lote.");
            ToastMessage({ cod: "e", texto: "Falha ao buscar agendamentos em lote." });
          }
        } catch (err) {
          setError("Erro ao buscar agendamentos em lote.");
          ToastMessage({ cod: "e", texto: "Erro ao buscar agendamentos em lote." });
          console.error("Erro ao buscar agendamentos em lote:", err);
        }
      }
    };

    if (isOpen) {
        fetchAppointments();
    }
  }, [isOpen, groupId, appointments]);

  const handleEditClick = (app) => {
    const event = {
        event: {
            id: app.id,
            title: app.title,
            extendedProps: {
                description: app.description,
                services: app.services,
                customer: app.customer,
                user: app.user
            },
            start: new Date(app.initial_time),
            end: new Date(app.final_time),
        }
    };
    setSelectedAppointment(event);
    setEditModalOpen(true);
  };

  const handleBulkEditClick = useCallback(() => {
    // CORREÇÃO: Verificação de segurança para evitar erro se a lista estiver vazia
    if (appointmentsList && appointmentsList.length > 0) {
        const firstAppointment = appointmentsList[0];
        const allDates = appointmentsList.map(app => app.initial_time);
        const event = {
            event: {
                id: firstAppointment.id,
                title: firstAppointment.title,
                extendedProps: {
                    description: firstAppointment.description,
                    services: firstAppointment.services,
                    customer: firstAppointment.customer,
                    user: firstAppointment.user,
                    allDates: allDates,
                },
                start: new Date(firstAppointment.initial_time),
                end: new Date(firstAppointment.final_time),
            }
        };
        setSelectedAppointment(event);
        setIsBulkEditingMode(true);
        setEditModalOpen(true);
    } else {
        ToastMessage({ cod: "w", texto: "Não há agendamentos para editar." });
    }
  }, [appointmentsList]);

  const handleCloseEditModal = () => {
      setEditModalOpen(false);
      setSelectedAppointment(null);
      setIsBulkEditingMode(false);
  };

  const handleSaveAppointment = async (appointmentData) => {
    if (isBulkEditingMode) {
        try {
            const { title, description, customer, user, services } = appointmentData;

            const payload = {
                title,
                description,
                customer,
                user,
                services,
                appointments: appointmentData.appointments,
            };

            const response = await appointments.updateBulk(groupId, payload);
            if (response.ok) {
                ToastMessage({ cod: "s", texto: "Agendamentos atualizados com sucesso" });
                setEditModalOpen(false);
                setIsBulkEditingMode(false);
                setHasChanged(true);
                const result = await appointments.getBulk(groupId);
                if (result.ok) {
                    const appointmentsArray = Object.values(result.data);
                    setAppointmentsList(appointmentsArray);
                }
            } else if (response.message) {
                ToastMessage({ cod: "e", texto: response.message });
            } else {
                ToastMessage({ cod: "e", texto: "Erro ao atualizar agendamentos." });
            }
        } catch (error) {
            console.error("handleSaveAppointment (bulk): Erro ao atualizar agendamentos:", error);
            ToastMessage({ cod: "e", texto: "Ocorreu um erro ao atualizar os agendamentos." });
        }
    } else {
        try {
            let response;
            if (appointmentData.id) {
                response = await appointments.update(appointmentData.id, appointmentData);
                if (response.ok) {
                    ToastMessage({ cod: "s", texto: "Agendamento atualizado com sucesso" });
                    setEditModalOpen(false);
                    setHasChanged(true);
                    const result = await appointments.getBulk(groupId);
                    if (result.ok) {
                        const appointmentsArray = Object.values(result.data);
                        setAppointmentsList(appointmentsArray);
                    }
                } else if (response.message) {
                    ToastMessage({ cod: "e", texto: response.message });
                } else {
                    ToastMessage({ cod: "e", texto: "Erro ao atualizar agendamento." });
                }
            }
        } catch (error) {
            console.error("handleSaveAppointment: Erro ao salvar agendamento:", error);
            ToastMessage({ cod: "e", texto: "Ocorreu um erro ao salvar o agendamento." });
        }
    }
  };

  const handleDeleteAppointment = async (id) => {
      try {
          const confirmed = await modalRef.current.showModal();
          if (confirmed) {
              const response = await appointments.inactivate(id);
              setEditModalOpen(false);
              if (response.ok) {
                  ToastMessage({ cod: "s", texto: "Agendamento excluído com sucesso" });
                  setHasChanged(true);
                  const result = await appointments.getBulk(groupId);
                  if (result.ok) {
                      const appointmentsArray = Object.values(result.data);
                      setAppointmentsList(appointmentsArray);
                  }
              } else if (response.message) {
                  ToastMessage({ cod: "e", texto: response.message });
              } else {
                  ToastMessage({ cod: "e", texto: "Erro ao excluir agendamento." });
              }
          }
      } catch (error) {
          console.error("handleDeleteAppointment: Erro ao excluir agendamento:", error);
          ToastMessage({ cod: "e", texto: "Ocorreu um erro ao excluir o agendamento." });
      }
  };

  const handleBulkDelete = useCallback(async () => {
    try {
      const confirmed = await modalRef.current.showModal();
      if (confirmed) {
        const response = await appointments.deleteBulk(groupId);
        if (response.ok) {
          ToastMessage({ cod: "s", texto: "Agendamentos excluídos com sucesso" });
          setHasChanged(true);
          onClose(true);
        } else if (response.message) {
          ToastMessage({ cod: "e", texto: response.message });
        } else {
          ToastMessage({ cod: "e", texto: "Erro ao excluir agendamentos." });
        }
      }
    } catch (error) {
      console.error("handleBulkDelete: Erro ao excluir agendamentos:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao excluir os agendamentos." });
    }
  }, [appointments, groupId, onClose]);

  const handleClose = useCallback(() => {
    onClose(hasChanged);
  }, [onClose, hasChanged]);

  const modalContent = useMemo(() => (
    <div className={style.modal_content}>
      <div className={style.modalHeader}>
        <h2>Agendamentos em Lote</h2>
        <span className={style.close} onClick={handleClose}>
          ×
        </span>
      </div>

      <div className={style.modalBody}>
        {load.isLoading && <><p>Carregando agendamentos...</p><CircuitLoader /></>}
        {error && <p>{error}</p>}
        {!load.isLoading && !error && appointmentsList.length === 0 && (
          <p>Nenhum agendamento encontrado para este grupo.</p>
        )}
        {!load.isLoading && <div className={style.cardsContainer}>
          {appointmentsList.map((app) => { 
            const appointmentDate = new Date(app.initial_time);
            
            // Formatando Data
            const dia = String(appointmentDate.getDate()).padStart(2, '0');
            const mes = String(appointmentDate.getMonth() + 1).padStart(2, '0');
            const ano = appointmentDate.getFullYear();
            
            // Formatando Hora
            const hora = String(appointmentDate.getHours()).padStart(2, '0');
            const minuto = String(appointmentDate.getMinutes()).padStart(2, '0');
            
            return(
              <div key={app.id} className={style.appointmentCard} onClick={() => handleEditClick(app)}>
                <h4>{app.title}</h4>
                <p><strong>Cliente:</strong> {app.customer}</p>
                <p><strong>Data:</strong> {dia}/{mes}/{ano}</p>
                <p><strong>Horário:</strong> {hora}:{minuto}</p>
                <p><strong>Funcionário:</strong> {app.user ? app.user.fullname : 'Não atribuído'}</p>
              
                  <strong>Serviços:</strong>
                  <ul>
                    {app.services && Object.values(app.services).map(service => (
                      <li key={service.id}>{service.name}</li>
                    ))}
                  </ul>
              
              </div>
            )
          })}
        </div>}
      </div>

      <div className={style.modalFooter}>
        <>
            <button
                className={`${style.modalButton} ${style.editButton}`}
                type="button"
                onClick={handleBulkEditClick}
                disabled={!appointmentsList || appointmentsList.length === 0}
            >
                Editar Todos
            </button>
            <button
                className={`${style.modalButton} ${style.deleteButton}`}
                type="button"
                onClick={handleBulkDelete}
                disabled={!appointmentsList || appointmentsList.length === 0}
            >
                Excluir Todos
            </button>
        </>
        <button
          className={`${style.modalButton} ${style.closeButton}`}
          type="button"
          onClick={handleClose}
        >
          Fechar
        </button>
      </div>
    </div>
  ), [appointmentsList, error, load.isLoading, handleClose, handleBulkEditClick, handleBulkDelete]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <ModalConfirmaExclusao ref={modalRef} />
      <div
        className={style.modal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        {modalContent}
      </div>
      {editModalOpen && (
        <AgendamentoModal
          onClose={handleCloseEditModal}
          onSave={handleSaveAppointment}
          onDelete={handleDeleteAppointment}
          selectedEvent={selectedAppointment}
          selectedDate={isBulkEditingMode ? appointmentsList.map(app => new Date(app.initial_time)) : [selectedAppointment.event.start]}
          isBulk={isBulkEditingMode}
        />
      )}
    </>
  );
};

export default BulkAppointmentsModal;